import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  AdminBanner,
  CreateBannerPayload,
  UpdateBannerPayload,
} from "~/api/banner.types";
import { bannersFilters, useQueryBuilder } from "~/hooks";
import { MainLayout, MODAL_ROUTES, useNavigateModal } from "~/router";
import { Button } from "~/shared";
import { mapLaravelPagination } from "~/utils";
import { SearchInput, Title } from "../components";
import { usePermissions } from "../hooks/usePermissions";
import { BannerFormModal } from "./banner-form-modal";
import { BannerTable } from "./banner-table";
import { DeleteConfirmDialog } from "./delete-confirm-dialog";
import {
  useCreateBanner,
  useDeleteBanner,
  useListBanners,
  useToggleBanner,
} from "./hooks/use-banner-admin";
import { ToggleConfirmDialog } from "./toggle-confirm-dialog";

export const BannerManagementPage = () => {
  const { canManageBanners } = usePermissions();
  const [searchParams] = useSearchParams();
  const querySearch = searchParams.get("search") ?? "";
  const navigateModal = useNavigateModal();

  const {
    builder: bannersBuilder,
    setCurrentPage,
    setSorting,
    sortingState,
    setFilters,
  } = useQueryBuilder({
    builderFilters: [{ attribute: "search", value: [querySearch] }],
    aliases: bannersFilters,
  });

  const { data, isLoading } = useListBanners({ queryBuilder: bannersBuilder });
  const banners = data?.data;
  const activeBanner = data?.active_banner ?? null;
  const pagination = useMemo(
    () => (data ? mapLaravelPagination(data.meta, data.links) : undefined),
    [data],
  );
  const createMutation = useCreateBanner();
  const deleteMutation = useDeleteBanner();
  const toggleMutation = useToggleBanner();

  const [newBannerOpen, setNewBannerOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<AdminBanner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminBanner | null>(null);
  const [search, setSearch] = useState(decodeURIComponent(querySearch));

  const handleToggle = (banner: AdminBanner) => {
    if (
      !banner.is_active &&
      activeBanner !== null &&
      activeBanner.id !== banner.id
    ) {
      setToggleTarget(banner);
    } else {
      toggleMutation.mutate(banner.id);
    }
  };

  const handleToggleConfirm = () => {
    if (toggleTarget) {
      toggleMutation.mutate(toggleTarget.id, {
        onSuccess: () => setToggleTarget(null),
      });
    }
  };

  const handleCreate = (data: CreateBannerPayload | UpdateBannerPayload) => {
    createMutation.mutate(data as CreateBannerPayload, {
      onSuccess: () => setNewBannerOpen(false),
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setFilters([{ attribute: "search", value: [encodeURIComponent(search)] }]);
  };

  const handleRowClick = (banner: AdminBanner) => {
    navigateModal(`${MODAL_ROUTES.bannerDetails}/${banner.id}`);
  };

  return (
    <MainLayout>
      <div className="flex flex-col justify-between pb-8 md:flex-row">
        <h1 className="font-ivy text-indigo text-[32px]">Banner Management</h1>

        <div className="flex flex-col gap-4 md:flex-row">
          {canManageBanners && (
            <Button
              variant="secondary"
              size="sm"
              className="gap-2 text-base"
              onClick={() => setNewBannerOpen(true)}
            >
              New Banner
            </Button>
          )}
          <SearchInput
            search={search}
            setSearch={setSearch}
            handleOnSubmit={handleSearch}
            placeholder="Search banners..."
          />
        </div>
      </div>

      <Title title="Banners" className="pb-4" titleClassName="text-indigo" />

      <hr className="border-soft-pastel-lavender" />

      <div className="pt-3">
        <BannerTable
          banners={banners}
          pagination={pagination}
          isLoading={isLoading}
          canManage={canManageBanners}
          sorting={sortingState}
          setSorting={setSorting}
          setCurrentPage={setCurrentPage}
          onRowClick={handleRowClick}
          onDelete={setDeleteTarget}
          onToggle={handleToggle}
        />
      </div>

      <BannerFormModal
        show={newBannerOpen}
        readOnly={!canManageBanners}
        onClose={() => setNewBannerOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <ToggleConfirmDialog
        show={toggleTarget !== null}
        onConfirm={handleToggleConfirm}
        onClose={() => setToggleTarget(null)}
        isLoading={toggleMutation.isPending}
      />

      <DeleteConfirmDialog
        show={deleteTarget !== null}
        banner={deleteTarget}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
        isLoading={deleteMutation.isPending}
      />
    </MainLayout>
  );
};
